<template>
  <div class="row map-list-container">
    <!-- search bar -->
    <div class="local-search">
      <div class="local-search-bar">
        <gmap-autocomplete @place_changed="handleAutocompleteUpdate">
        </gmap-autocomplete>
      </div>
      <div class="local-search-my-location">
        <img
          src="https://assets.vendie.io/logos/crosshairs.png"
          class="location-crosshair"
          @click="handleUserLocationUpdate"
        />
      </div>
    </div>

    <!-- map -->
    <div class="local-map-container">
      <g-map-map
        :center="this.center"
        :zoom="this.zoom"
        map-type-id="terrain"
        ref="map"
        :options="options"
        class="maps-responsive-height"
      >
        <gmap-info-window
          :options="infoOptions"
          :position="infoWindowPos"
          :opened="infoWinOpen"
          @closeclick="infoWinOpen = false"
        >
          <div class="info-window">
            <div>
              <b>{{ infoContent.name }}</b>
            </div>
            <div>{{ infoContent.street }}</div>
            <a
              :href="`https://maps.google.com/maps?daddr=${infoContent.name} ${infoContent.street}`"
              @click="routeDatalayerPush(infoContent)"
              >Route</a
            >
          </div>
        </gmap-info-window>
        <g-map-marker
          :key="index"
          v-for="(m, index) in markers"
          :position="m.position"
          :icon="customIcon"
          :clickable="true"
          @click="toggleInfoWindow(m, index)"
        />
      </g-map-map>
    </div>

    <!-- list -->
    <div class="store-list-container">
      <local-offline-map-store-list
        :markers="markers"
        @storeSelected="
          (store) => {
            handleStoreUpdate(store)
          }
        "
      />
    </div>
    <p class="disclaimer" v-if="disclaimer">{{ disclaimer }}</p>
  </div>
</template>

<script>
import LocalOfflineMapStoreList from '@/components/localPopup/localOfflineMapStoreList'
import {
  Map,
  Marker,
  Autocomplete,
  InfoWindow,
  gmapApi,
} from 'vue2-google-maps'

export default {
  name: 'localOfflineMap',
  props: {
    localData: {
      type: Array,
    },
    customIcon: { type: String },
    startingLat: { type: String },
    startingLng: { type: String },
    startingZoom: { type: Number },
    enableClustering: { type: Boolean }, // not used
    disclaimer: { type: String }, // not used
  },
  components: {
    LocalOfflineMapStoreList,
    gMapMap: Map,
    gMapMarker: Marker,
    gmapAutocomplete: Autocomplete,
    gmapInfoWindow: InfoWindow,
  },
  data() {
    return {
      center: { lat: 52.2961000020494, lng: 5.503440002334412 },
      zoom: 6,
      infoContent: {},
      infoWindowPos: null,
      infoWinOpen: false,
      currentMidx: null,
      infoOptions: {
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
      markers: [],
      options: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        disableDefaultUi: false,
      },
      defaultAutocompleteZoom: 7,
      defaultUserZoom: 12,
      defaultStoreZoom: 14,
    }
  },
  computed: {
    google: gmapApi,
  },
  methods: {
    toggleInfoWindow(marker, idx) {
      // idx is marker index
      this.infoWindowPos = marker.position
      this.infoContent = marker.infoText
      if (this.currentMidx === idx) {
        this.infoWinOpen = !this.infoWinOpen
      } else {
        this.infoWinOpen = true
        this.currentMidx = idx

        this.dataLayerPush('vendie_view_store', {
          storeName: marker.infoText.name,
          storeCity: marker.infoText.city,
          storeStreet: marker.infoText.street,
        })
      }
    },

    updateMarkerDistances() {
      this.markers.forEach((marker) => {
        let center = new this.google.maps.LatLng(this.center)
        let targetLoc = new this.google.maps.LatLng(marker.position)
        let distance = this.google.maps.geometry.spherical.computeDistanceBetween(
          center,
          targetLoc
        )
        marker['distance'] = distance
      })
    },

    setCenterByPlace(place) {
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      this.center = { lat, lng }
    },

    setCenter(lat, lng) {
      this.center = {
        lat,
        lng,
      }
    },

    // convertRadius() { // disabled, chose to use all markers all the time
    //   // this.radius = Number(this.radius) * 1609.344;
    //   this.radius = 1000 * 1609.344 // dirty // !!!
    // },

    // filterMarkers() { // disabled, chose to use all markers all the time
    //   this.markers = this.markers.filter(
    //     (marker) => marker.distance < this.radius
    //   )
    // },

    sortMarkers() {
      this.markers.sort((a, b) => a.distance - b.distance)
    },

    updateLocationsList() {
      // this.convertRadius() // disabled, chose to use all markers all the time
      // this.filterMarkers() // disabled, chose to use all markers all the time
      this.updateMarkerDistances()
      this.sortMarkers()
    },

    handleAutocompleteUpdate(place) {
      this.setCenterByPlace(place)
      this.updateLocationsList()
      this.zoom = this.defaultStoreZoom
    },

    handleUserLocationUpdate() {
      navigator.geolocation.getCurrentPosition((position) => {
        this.setCenter(position.coords.latitude, position.coords.longitude)
        this.zoom = this.defaultUserZoom
        this.updateLocationsList()
      })
    },

    handleStoreUpdate(store) {
      const lat = store.position.lat
      const lng = store.position.lng

      this.setCenter(lat, lng)
      this.updateLocationsList()

      this.zoom = this.defaultStoreZoom
      this.infoWinOpen = false
      this.infoWindowPos = store.position
      this.infoContent = store.infoText
      this.infoWinOpen = true

      this.dataLayerPush('vendie_view_store', {
        storeName: store.infoText.name,
        storeCity: store.infoText.city,
        storeStreet: store.infoText.street,
      })
    },

    dataLayerPush(eventName, keyVals) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: eventName,
        ...keyVals,
      })
    },

    routeDatalayerPush(infoContent) {
      this.dataLayerPush('vendie_store_route', {
        storeName: infoContent.name,
        storeCity: infoContent.city,
        storeStreet: infoContent.street,
      })
    },
  },

  created() {
    this.center = {
      lat: parseFloat(this.startingLat),
      lng: parseFloat(this.startingLng),
    }
    this.zoom = this.startingZoom
    this.localData.map((element) => {
      this.markers.push({
        position: {
          lat: Number(element.lat),
          lng: Number(element.lng),
        },
        infoText: {
          name: element.name,
          city: element.city,
          street: element.street,
        },
      })
    })
  },
}
</script>

<style scoped>
.vue-map-container {
  display: inline-block;
  width: 100%;
}

.local-search {
  padding: 12px 0;
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: auto 34px;
  align-items: center;
  gap: 2px;
}

.local-search button {
  margin-left: 10px;
  padding: 2px 0.75rem;
}

.local-search input {
  width: 100%;
  box-sizing: border-box;
  font-family: 'Open Sans', sans-serif !important;
  padding: 1rem 1.2rem;
  font-weight: 400;
}

.local-search-my-location {
  text-align: center;
}

/* .local-search label {
  margin-bottom: 0;
  width: 90%;
} */

.local-search .location {
  width: 5px;
  margin-right: 5px;
}

/* b */
.store-list-container,
.local-map-container {
  padding-left: 0;
  padding-right: 0;
}

.local-map-container {
  line-height: 0 !important; /* to remove whitespace below map */
}

.location-crosshair {
  width: 24px;
}

.location-crosshair:hover {
  cursor: pointer;
}

.disclaimer {
  color: gray;
}

.maps-responsive-height {
  width: 100%;
  height: 250px;
}

/* For mobile devices */
@media screen and (max-width: 767px) {
  .maps-responsive-height {
    height: 200px;
  }
}
</style>
